import { useBreakpointIndex } from '@theme-ui/match-media';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Route, Routes, useLocation } from 'react-router';

import { isMobile } from 'utils/responsive';
import { parseIntOrNull } from 'utils/stringHelpers';

import { ViewPanel } from 'components/_controls/ViewPanel';

import { setDiseaseActivityParams } from 'diseaseActivityPanels/diseaseActivityPanels.saga';
import diseaseActivityPanelsSelectors from 'diseaseActivityPanels/diseaseActivityPanels.selectors';
import DiseasesContainer from 'diseases/DiseasesContainer';

import { minimizePanel, PanelType } from '../diseaseActivityPanels/diseaseActivityPanels.slice';
import { LocationsList } from './components/LocationsList';
import { LocationsToolbar } from './components/LocationsToolbar';
import locationsSelectors from './locations.selectors';

const LocationsContainer: React.FC = () => {
  const dispatch = useDispatch();
  const isMobileDevice = isMobile(useBreakpointIndex());
  const { pathname } = useLocation();

  useEffect(() => {
    const geonameId = pathname.match(/location\/(\d+)/)?.[1];
    const [, diseaseId, speciesId] = pathname.match(/\/disease\/(\d+)\/species\/(\d+)/) ?? [];
    const countryId = pathname.match(/\/country\/(\d+)/)?.[1];
    const activityDate = pathname.match(/\/activity\/(\d{4}-\d{2}-\d{2})/)?.[1];

    const hasActivity = activityDate?.length || pathname.includes('/activity');
    const hasParameters = pathname.includes('/parameters');

    dispatch(
      setDiseaseActivityParams({
        userLocationId: parseIntOrNull(geonameId),
        diseaseId: parseIntOrNull(diseaseId),
        speciesId: parseIntOrNull(speciesId),
        countryId: parseIntOrNull(countryId),
        activityDate,
        hasActivity,
        hasParameters,
      })
    );
  }, [pathname]);

  const { canClose, isMinimized } = useSelector(
    diseaseActivityPanelsSelectors.getPanelVisibility(PanelType.MyLocationsPanel)
  );

  const isLoading = useSelector(locationsSelectors.isLoading);
  const hasError = useSelector(locationsSelectors.hasError);
  const activePanel = useSelector(diseaseActivityPanelsSelectors.activePanel);
  const selectedLocationTitle = useSelector(locationsSelectors.getSelectedLocationTitle);

  const showPanel =
    !isMobileDevice || (isMobileDevice && activePanel === PanelType.MyLocationsPanel);

  const onMinimize = (flag: boolean): void => {
    dispatch(minimizePanel(PanelType.MyLocationsPanel, flag));
  };

  return (
    <>
      {showPanel && (
        <ViewPanel
          title="My Locations"
          subtitle={selectedLocationTitle}
          canClose={canClose}
          isMinimized={isMinimized}
          onMinimize={onMinimize}
          isLoading={isLoading}
          hasError={hasError}
          toolbar={<LocationsToolbar />}
        >
          <LocationsList />
        </ViewPanel>
      )}
      <Routes>
        <Route path=":geonameId/*" element={<DiseasesContainer />} />
      </Routes>
    </>
  );
};

export default LocationsContainer;
